
import { useState, useEffect } from 'react';

// Define a type that extends Navigator to include the standalone property
interface SafariNavigator extends Navigator {
  standalone?: boolean;
}

/**
 * Custom hook to check if the application is running as a PWA
 * @returns an object with isPwa flag and installPrompt handler
 */
export const usePwaStatus = () => {
  const [isPwa, setIsPwa] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<Event | null>(null);

  useEffect(() => {
    // Function to check if app is running as PWA
    const checkPwaStatus = () => {
      // Check if running in standalone mode (installed on home screen)
      const isStandalone = (window.navigator as SafariNavigator).standalone || 
                           window.matchMedia('(display-mode: standalone)').matches;
      setIsPwa(isStandalone);
    };

    // Check PWA status on mount
    checkPwaStatus();

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent Chrome <= 67 from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later
      setInstallPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', checkPwaStatus);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', checkPwaStatus);
    };
  }, []);

  return { isPwa, installPrompt };
};

export default usePwaStatus;
